import React, { useEffect, useState } from "react";
import { Briefcase, GraduationCap, Banknote } from "lucide-react";
import api, { errMsg } from "../api";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>{children}</div>;
}

export default function MyProgram() {
  const [program, setProgram] = useState(null);
  const [wf, setWf] = useState(null);
  const [patriot, setPatriot] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  const register = async () => {
    setBusy(true);
    try {
      const r = await api.post("/my/training/register");
      setWf((w) => ({ ...(w || {}), stage: r.data.stage }));
      toast({ title: "Registered for training", description: "Your team will share the schedule." });
    } catch (e) { toast({ title: "Could not register", description: errMsg(e), variant: "destructive" }); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    Promise.all([
      api.get("/my/program").then((r) => setProgram(r.data)).catch(() => {}),
      api.get("/my/workforce-status").then((r) => setWf(r.data)).catch(() => {}),
      api.get("/my/patriot").then((r) => setPatriot(r.data)).catch(() => {}),
    ]).finally(() => setLoaded(true));
  }, []);

  const money = (v) => (v == null ? "Pending" : `$${Number(v).toFixed(2)}`);
  if (!loaded) return <div className="text-sm text-slate-400">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="my-program">
      <Card>
        <div className="flex items-center gap-2"><GraduationCap className="h-4 w-4 text-violet-500" /><h2 className="text-base font-bold text-slate-900">My Training &amp; Readiness</h2></div>
        <div className="mt-3 flex flex-wrap gap-3">
          <div className="rounded-lg border border-slate-100 px-4 py-3"><div className="text-xs uppercase text-slate-400">Status</div><div className="font-semibold text-slate-800" data-testid="my-stage">{wf?.stage || "Not Registered"}</div></div>
          {wf?.training_scheduled_date && <div className="rounded-lg border border-slate-100 px-4 py-3"><div className="text-xs uppercase text-slate-400">Scheduled</div><div className="font-semibold text-slate-800" data-testid="my-training-date">{wf.training_scheduled_date}</div></div>}
        </div>
        {Object.entries(program?.training_requirements || {}).filter(([, v]) => v).length > 0 && (
          <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-600" data-testid="my-training-requirements">
            {Object.entries(program.training_requirements).filter(([, v]) => v).map(([k, v]) => <div key={k}><span className="capitalize text-slate-400">{k.replace(/_/g, " ")}:</span> {String(v)}</div>)}
          </div>
        )}
        {program && (wf?.stage || "Not Registered") === "Not Registered" && (
          <Button onClick={register} disabled={busy} className="btn-primary-gradient mt-4 border-0 text-white" data-testid="register-training-btn">{busy ? "Registering…" : "Register for program training"}</Button>
        )}
        <p className="mt-3 text-xs text-slate-400">Training progress after registration is only updated from information your client or onboarding team confirms.</p>
      </Card>

      {program ? (
        <Card data-testid="my-assignment">
          <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-violet-500" /><h2 className="text-base font-bold text-slate-900">My Program</h2></div>
          <div className="mt-2 font-semibold text-slate-900">{program.name}</div>
          <div className="text-sm text-slate-500">{program.job_title} · {program.work_type} · {program.schedule}</div>
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3">
            <Banknote className="h-4 w-4 text-emerald-600" />
            <div>
              <div className="text-xs uppercase text-emerald-700">Your pay rate</div>
              <div className="font-bold text-emerald-800" data-testid="my-pay-rate">{money(program.contractor_pay_rate)} {program.pay_type ? `· ${program.pay_type}` : ""} {program.payment_frequency ? `· ${program.payment_frequency}` : ""}</div>
              <div className="text-xs text-emerald-700">Training pay: {money(program.contractor_training_pay_rate)}</div>
            </div>
          </div>
        </Card>
      ) : (
        <Card><p className="text-sm text-slate-400">You haven't been assigned to a client program yet.</p></Card>
      )}

      <Card>
        <h3 className="text-sm font-bold text-slate-900">Patriot Software setup</h3>
        <p className="text-sm text-slate-500">Status: <strong data-testid="my-patriot">{patriot?.patriot_status || "Not Started"}</strong>. When ready, you'll manage direct deposit securely in Patriot's own Contractor Portal.</p>
      </Card>
    </div>
  );
}
